/**
 * CommandPalette — global Cmd/Ctrl+K menu.
 *
 * Mounts ONCE at the App root, listens for Cmd/Ctrl+K (and the `/`
 * key when no input is focused) to toggle, and offers two groups:
 *
 *   • **Navegación** — every primary route in the app, fuzzy-searchable
 *     by route name (Spanish/English aware via `lang`).
 *   • **Acciones** — quick verbs like "Agregar movimiento", "Ver
 *     reporte semanal", "Cerrar sesión".
 *
 * Why an additional surface on top of the sidebar: heavy users keep
 * their hands on the keyboard. The sidebar is still primary for
 * discoverability, but power users get a 200ms path to any screen.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem,
} from "./ui/command";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import {
  Home, PlusCircle, Receipt, Wallet, History as HistoryIcon,
  LineChart, CreditCard, Target, Banknote, FileText, CalendarDays,
  Brain, MessagesSquare, BookOpen, User2, Sparkles, ShieldCheck,
  LogOut, Search as SearchIcon, Keyboard,
} from "lucide-react";

function trx(lang, en, es) {
  return lang === "en" ? en : es;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const { lang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Global keyboard shortcut: Cmd/Ctrl+K opens. ESC inside the
  // CommandDialog already closes — no separate handler needed.
  useEffect(() => {
    function onKey(e) {
      const isMeta = e.metaKey || e.ctrlKey;
      if (isMeta && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const navItems = useMemo(() => {
    const base = [
      { id: "/dashboard",       label: trx(lang, "Home", "Inicio"),                icon: Home },
      { id: "/add",             label: trx(lang, "Add movement", "Agregar movimiento"), icon: PlusCircle },
      { id: "/receivables",     label: trx(lang, "Receivables", "Cobros"),         icon: Receipt },
      { id: "/payments",        label: trx(lang, "Payments", "Pagos"),             icon: Wallet },
      { id: "/history",         label: trx(lang, "History", "Movimientos"),        icon: HistoryIcon },
      { id: "/accounts",        label: trx(lang, "Accounts", "Cuentas"),           icon: Banknote },
      { id: "/investments",     label: trx(lang, "Investments", "Inversiones"),    icon: LineChart },
      { id: "/debts",           label: trx(lang, "Debts", "Deudas"),               icon: CreditCard },
      { id: "/goals",           label: trx(lang, "Goals", "Metas"),                icon: Target },
      { id: "/report",          label: trx(lang, "Report", "Reporte"),             icon: FileText },
      { id: "/calendar",        label: trx(lang, "Calendar", "Calendario"),        icon: CalendarDays },
      { id: "/recommendations", label: trx(lang, "Recommendations", "Recomendaciones"), icon: Brain },
      { id: "/integrations",    label: trx(lang, "Integrations", "Integraciones"), icon: MessagesSquare },
      { id: "/bots-guide",      label: trx(lang, "Bots guide", "Guía de bots"),    icon: BookOpen },
      { id: "/my-plan",         label: trx(lang, "My plan", "Mi plan"),            icon: CreditCard },
      { id: "/profile",         label: trx(lang, "My profile", "Mi perfil"),       icon: User2 },
      { id: "/pricing",         label: trx(lang, "Plans & packs", "Planes y packs"), icon: Sparkles },
    ];
    if (user?.is_admin) {
      base.push({ id: "/admin", label: trx(lang, "Admin panel", "Panel admin"), icon: ShieldCheck });
    }
    return base;
  }, [lang, user?.is_admin]);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        data-testid="command-palette-input"
        placeholder={trx(lang, "Type a command or search…", "Busca o escribe un comando…")}
      />
      <CommandList>
        <CommandEmpty data-testid="command-palette-empty">
          <span className="inline-flex items-center gap-1.5 text-slate-400">
            <SearchIcon className="h-3.5 w-3.5" />
            {trx(lang, "No results", "Sin resultados")}
          </span>
        </CommandEmpty>
        <CommandGroup heading={trx(lang, "Navigate", "Navegación")}>
          {navItems.map(({ id, label, icon: Icon }) => (
            <CommandItem
              key={id}
              value={`${label} ${id}`}
              onSelect={() => go(id)}
              data-testid={`command-palette-nav-${id.replace(/\//g, "")}`}
            >
              <Icon className="h-4 w-4 mr-2 text-slate-500" />
              <span>{label}</span>
              <span className="ml-auto text-[10px] uppercase font-mono text-slate-400">{id}</span>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandGroup heading={trx(lang, "Actions", "Acciones")}>
          <CommandItem
            value="logout cerrar sesion"
            onSelect={async () => {
              setOpen(false);
              await logout();
              navigate("/");
            }}
            data-testid="command-palette-logout"
          >
            <LogOut className="h-4 w-4 mr-2 text-rose-500" />
            <span>{trx(lang, "Sign out", "Cerrar sesión")}</span>
          </CommandItem>
        </CommandGroup>
        <div className="border-t px-3 py-2 flex items-center gap-2 text-[10px] text-slate-400">
          <Keyboard className="h-3 w-3" />
          {trx(lang, "Cmd/Ctrl + K to toggle · Esc to close", "Cmd/Ctrl + K para abrir · Esc para cerrar")}
        </div>
      </CommandList>
    </CommandDialog>
  );
}
